import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {request} from '@/data/util/promiseRequest'
import {minutesToMilliseconds} from '@/utils/time'

export type GetResourceBudgetRequestParams = {
  organizationId: string
}

export type ResourceUsage = {
  timestamp: string
  metric: string
  value: number
  periodStart: string
  periodEnd: string
}

export type ResourceBudget = {
  maxAmount: number | null
  used: number
  period: string
  notificationThreshold: number
}

export type GetResourceBudgetResponse = {
  id: string
  name: string
  description: string
  defaultMaxAmount: number
  usage: ResourceUsage[]
  budget: ResourceBudget | null
}

export type GetBudgetsResponse = {
  pagination: {
    nextCursor: string
    hasMore: boolean
  }
  budgets: GetResourceBudgetResponse[]
}

export const getBudgetsQueryKey = (organizationId: string) => [
  'budgets',
  'customer',
  organizationId,
]

export function useBudgets(params: GetResourceBudgetRequestParams, options?: {enabled?: boolean}) {
  return useQuery({
    queryKey: getBudgetsQueryKey(params.organizationId),
    queryFn: async () => {
      return request<GetBudgetsResponse>({
        headers: {'Content-Type': 'application/json'},
        url: `https://api.${process.env.host}/vX/budgets/customers/${params.organizationId}`,
        method: 'GET',
      }).then((res) => {
        if (!res.ok) {
          throw new Error(res.body.message)
        }
        return res.body
      })
    },
    staleTime: minutesToMilliseconds(5),
    enabled: options?.enabled ?? true,
  })
}

export type PutResourceBudgetRequestParams = {
  maxAmount: number | null
}

export function useSetBudget(params: {organizationId: string; groupId: string}) {
  const {organizationId, groupId} = params
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['budgets', 'customer', groupId, organizationId],
    mutationFn: async (data: PutResourceBudgetRequestParams) => {
      return request<GetResourceBudgetResponse>({
        headers: {'Content-Type': 'application/json'},
        url: `https://api.${process.env.host}/vX/budgets/customers/${organizationId}/${groupId}`,
        method: 'PUT',
        body: data,
      }).then((res) => {
        if (!res.ok) {
          throw new Error(res.body.message)
        }
        return res.body
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getBudgetsQueryKey(params.organizationId),
      })
    },
  })
}
